'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { AlertContext, errorBanner } from '../alert';
import { Logger } from '../../lib/logger';
import { useSessionStorage } from '../../lib/useSessionStorage';
import { noop } from 'lodash';
export const AuthContext = createContext({
    useContextUser: () => ({}),
    setContextUser: (user) => { },
    onAuthSuccess: (user, sessionId) => { },
    useIsLoggedIn: () => Boolean(false),
    authenticate: (input, redirectUrl) => { },
    restoreAuthSession: () => { },
    logout: () => { },
    useAuthState: () => ({}),
    useAuthToken: () => ({})
});
export const AuthProvider = ({ children, redirectHandler = noop, authenticateGraphQLDocument, contextUserGraphQLDocument }) => {
    const [contextUser, setContextUser] = useState(null);
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const apollo = useApolloClient();
    const { pushAlert } = useContext(AlertContext);
    const [sessionToken, setSessionToken] = useSessionStorage('token', '');
    const getContextUser = async () => {
        try {
            const { data } = await apollo.query({
                query: gql(contextUserGraphQLDocument)
            });
            return data.me;
        }
        catch (e) {
            Logger.error('[AuthProvider] getContextUser', e);
        }
    };
    const restoreAuthSession = async () => {
        Logger.log('restoring auth session', sessionToken);
        try {
            if (sessionToken) {
                const user = await getContextUser();
                setContextUser(user);
                setLoading(false);
            }
        }
        catch (e) {
            Logger.error('[AuthProvider] restoreAuthSession', e);
        }
    };
    useEffect(() => {
        restoreAuthSession();
    }, []);
    const useContextUser = () => {
        return contextUser;
    };
    const onAuthSuccess = (user, sessionId) => {
        setContextUser(user);
    };
    const handleAuthenticationSuccess = ({ user, auth }, redirectUrl) => {
        setContextUser(user);
        setSessionToken(auth.token);
        setLoading(false);
        redirectHandler(redirectUrl);
    };
    const handleAuthenticationError = (e) => {
        Logger.error('[authenticate] error', e);
        pushAlert(errorBanner({
            title: 'Unauthorized',
            message: 'The email or password you entered is incorrect.'
        }));
    };
    const authenticationHandler = async (input, redirectUrl = '/my-account') => {
        try {
            const { data } = await apollo.mutate({
                mutation: gql(authenticateGraphQLDocument),
                variables: { input }
            });
            handleAuthenticationSuccess(data.authenticate, redirectUrl);
        }
        catch (e) {
            handleAuthenticationError(e);
        }
    };
    const logout = () => {
        setSessionToken('');
        setContextUser(null);
    };
    const useIsLoggedIn = () => {
        return !!sessionToken;
    };
    const useAuthToken = () => {
        return { token: sessionToken };
    };
    const useAuthState = () => {
        return {
            message,
            loading
        };
    };
    return (_jsx(AuthContext.Provider, { value: {
            useContextUser,
            setContextUser,
            onAuthSuccess,
            useIsLoggedIn,
            authenticate: authenticationHandler,
            restoreAuthSession,
            logout,
            useAuthState,
            useAuthToken
        }, children: children }));
};
